import { useId, useState } from 'react'
import { f2 } from '../copyFlow2'
import { seeItCity } from '../lib/plan'
import { ArrowUpRight, ChevronDown } from '../ui/icons'

/** Zero-cost link-outs, built in the browser from the place name (+ area) — nothing is fetched or stored.
 * Without an area or address they search in the plan's city (WP13: not always Los Angeles).
 * Google Places hybrid (10/09): a map place matched to a Google place id opens that exact place in Maps; else the search. */
export function seeItLinks(name: string, area?: string | null, address?: string | null, placeId?: string | null) {
  const city = seeItCity()
  const q = `${name} ${area?.trim() || city}`.trim()
  const enc = encodeURIComponent
  return [
    { site: 'tiktok', href: `https://www.tiktok.com/search?q=${enc(q)}` },
    { site: 'instagram', href: `https://www.instagram.com/explore/search/keyword/?q=${enc(q)}` },
    { site: 'reddit', href: `https://www.reddit.com/search/?q=${enc(q)}` },
    { site: 'youtube', href: `https://www.youtube.com/results?search_query=${enc(q)}` },
    { site: 'maps', href: placeId?.trim()
      ? `https://www.google.com/maps/place/?q=place_id:${enc(placeId.trim())}`
      : `https://www.google.com/maps/search/?api=1&query=${enc(`${name} ${address?.trim() || area?.trim() || city}`)}` },
  ] as const
}

function Links({ name, area, address, placeId }: { name: string; area?: string | null; address?: string | null; placeId?: string | null }) {
  return (
    <>
      {seeItLinks(name, area, address, placeId).map(({ site, href }) => (
        <a
          key={site}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={f2('seeit.aria', { site: f2(`seeit.${site}`), name })}
          className="press inline-flex min-h-11 shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full px-2 text-[14px] font-semibold text-ink hover:bg-sunk"
        >
          {f2(`seeit.${site}`)}
          <ArrowUpRight size={13} className="text-ink-2" />
        </a>
      ))}
    </>
  )
}

/**
 * "See it": TikTok, Instagram, Reddit, YouTube and Google Maps searches for one place, each opening in a new tab.
 * `wrap`: the links wrap onto a second line instead of running off the edge (narrow cards).
 */
export function SeeIt({ name, area, address, placeId, wrap = false, className = '' }: { name: string; area?: string | null; address?: string | null; placeId?: string | null; wrap?: boolean; className?: string }) {
  if (wrap) {
    return (
      <div className={className}>
        <p className="t-caption font-semibold text-ink-2">{f2('seeit.label')}</p>
        <div className="-ml-2 flex flex-wrap items-center gap-x-1">
          <Links name={name} area={area} address={address} placeId={placeId} />
        </div>
      </div>
    )
  }
  return (
    // the links wrap onto a second line on every screen: a swipe row hid YouTube and Google Maps past the edge with no cue (trip QA 2)
    <div className={`flex min-h-11 items-start gap-1 ${className}`}>
      <span className="t-caption shrink-0 pr-1 font-semibold leading-[44px] text-ink-2">{f2('seeit.label')}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1">
        <Links name={name} area={area} address={address} placeId={placeId} />
      </div>
    </div>
  )
}

/** One "Explore this place" disclosure: the five links stay folded until asked for, then wrap (never clip). */
export function SeeItDisclosure({ name, area, address, placeId, className = '' }: { name: string; area?: string | null; address?: string | null; placeId?: string | null; className?: string }) {
  const [open, setOpen] = useState(false)
  const id = useId()
  return (
    <div className={className}>
      <button type="button" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}
        className="press -ml-1 inline-flex min-h-11 items-center gap-1 rounded-full px-1 text-[15px] font-semibold text-ink">
        {f2('seeit.label')}
        <ChevronDown size={16} className={`text-ink-2 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div id={id} className="-ml-2 flex flex-wrap items-center gap-x-1">
          <Links name={name} area={area} address={address} placeId={placeId} />
        </div>
      )}
    </div>
  )
}
